import { setFormData } from 'reducers/productConfigurationReducer';
import {
  setContactNotes,
  setIsTableDataLoading,
  setModificationData,
  setSelectedServiceAgreement,
  setSelectedServiceAgreementAttributes,
  setTableData,
} from 'reducers/serviceAgreementsReducer';
import { getContactNotesByBrUidApi } from 'services/product-task/getContactNotesByBrUidApi';
import { sendModificationDataApi } from 'services/service-agreement/sendModificationDataApi';
import { getModificationDataApi } from 'services/service-agreement/getModificationDataApi';
import { closeServiceAgreementsLaterDateApi } from 'services/service-agreement/closeServiceAgreementsLaterDateApi';
import { closeServiceAgreementsApi } from 'services/service-agreement/closeServiceAgreementsApi';
import { moveForwardServiceAgreementProcessApi } from 'services/service-agreement/moveForwardServiceAgreementProcessApi';
import { updateServiceAgreementApi } from 'services/service-agreement/updateServiceAgreementApi';
import { getServiceAgreementApi } from 'services/service-agreement/getServiceAgreementApi';
import { createServiceAgreementApi } from 'services/service-agreement/createServiceAgreementApi';
import { getAllServiceAgreementsApi } from 'services/service-agreement/getAllServiceAgreementsApi';
import { parseServiceAgreementAttributes } from 'utils/serviceAgreementUtils';
import _ from 'lodash';
import { setLoader } from 'reducers/appConfigReducer';
import { setPartnerNotFound, setPartnerNotFoundComment } from 'reducers/partnerReducer';
import { fetchDocumentsToDistribute } from './productDocumentsThunk';
import { AppDispatch, RootState } from 'store';
import { closeModal, openModal } from 'reducers/modalReducer';
import {
  type ServiceAgreementMetadata,
  ServiceAgreementModificationInformationResponse,
  ServiceAgreementModificationRequest,
  ServiceAgreementV2,
} from 'pco-product-service-internal-consumer';

export const fetchAllServiceAgreementsForAccount =
  (
    partnerUid?: string | undefined,
    businessRelationUid?: string | undefined,
    accountNumber?: string | undefined,
    loader?: boolean,
  ) =>
  async (dispatch: AppDispatch) => {
    if (loader) dispatch(setIsTableDataLoading(true));

    return getAllServiceAgreementsApi(partnerUid, businessRelationUid, accountNumber)
      .then((response) => {
        const sorted = response?.list?.sort((a: ServiceAgreementV2, b: ServiceAgreementV2) => {
          return new Date(b.modifiedAt ?? 0).getTime() - new Date(a.modifiedAt ?? 0).getTime();
        });

        dispatch(setTableData(sorted ?? []));
        dispatch(setIsTableDataLoading(false));
      })
      .catch((error) => {
        dispatch(
          openModal({
            modalType: 'JBErrorModalWrapper',
            modalProps: {
              httpError: error,
              okHandler: () => {
                dispatch(closeModal());
              },
              technicalInfo: {
                title: 'Error when retrieving list of Service Agreements',
                userErrorMessage:
                  'An error occurred when retrieving list of Service Agreements. Please try again later.',
              },
            },
          }),
        );
        dispatch(
          setLoader({
            loading: false,
            message: '',
          }),
        );
        return 'error';
      })
      .finally(() => {
        dispatch(setIsTableDataLoading(false));
      });
  };

//TODO Mark - decouple this from the loading logic to prevent unwanted whole page loading issues
export const getServiceAgreement =
  (
    serviceId: string | undefined,
    processInstanceId: string | null,
    fetchAllAgreements: boolean,
    clearFormData = true,
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(
      setLoader({
        loading: true,
        message: 'Loading Service Agreement...',
      }),
    );

    const { brUid, accountDetails } = getState().account;
    const accountNumber = accountDetails?.accountNumber;

    if (fetchAllAgreements) {
      if (brUid && accountNumber) {
        dispatch(fetchAllServiceAgreementsForAccount(undefined, brUid, accountNumber, false));
      } else if (brUid && !accountNumber) {
        dispatch(fetchAllServiceAgreementsForAccount(undefined, brUid, undefined, false));
      }
    }

    const result = await fetchServiceAgreement(serviceId, processInstanceId ?? null, dispatch);

    if (clearFormData) {
      dispatch(setFormData(undefined));
    }
    dispatch(setSelectedServiceAgreement(result));
    dispatch(setSelectedServiceAgreementAttributes(parseServiceAgreementAttributes(result.attributes)));
    dispatch(
      setLoader({
        loading: false,
        message: '',
      }),
    );
  };

export const getSingleServiceAgreement =
  (serviceId: string | undefined, processInstanceId: string | null) => async (dispatch: AppDispatch) => {
    const result = await fetchServiceAgreement(serviceId, processInstanceId ?? null, dispatch);

    dispatch(setSelectedServiceAgreement(result));
    dispatch(setSelectedServiceAgreementAttributes(parseServiceAgreementAttributes(result.attributes)));
  };

export const createServiceAgreement =
  (partnerUid: string | undefined, businessRelationUid: string | undefined, serviceDefinitionUid: string) =>
  async (dispatch: AppDispatch) => {
    dispatch(
      setLoader({
        loading: true,
        message: 'Creating Service Agreement...',
      }),
    );
    createServiceAgreementApi(partnerUid, businessRelationUid, serviceDefinitionUid)
      .catch((error) => {
        dispatch(
          openModal({
            modalType: 'JBErrorModalWrapper',
            modalProps: {
              httpError: error,
              okHandler: () => {
                dispatch(closeModal());
              },
              technicalInfo: {
                title: 'Error when creating a new Service Agreement',
                userErrorMessage: 'An error occurred when creating a new Service Agreement. Please try again later.',
              },
            },
          }),
        );
        dispatch(
          setLoader({
            loading: false,
            message: '',
          }),
        );
        return;
      })
      .then(async (response) => {
        if (!response) return;
        const serviceAgreement = await fetchServiceAgreement(undefined, response?.processInstanceId, dispatch);

        dispatch(setSelectedServiceAgreement(serviceAgreement));
        dispatch(setFormData(undefined));
        dispatch(setSelectedServiceAgreementAttributes(parseServiceAgreementAttributes(serviceAgreement)));

        if (!businessRelationUid) return;
        dispatch(fetchAllServiceAgreementsForAccount(undefined, businessRelationUid, undefined));
        dispatch(fetchDocumentsToDistribute(businessRelationUid, null));
        dispatch(
          setLoader({
            loading: false,
            message: '',
          }),
        );
      });
  };

export const moveForwardServiceAgreement =
  (taskId: string | undefined, serviceAgreementUid: string, formData: string | undefined, action: string | undefined) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(
      setLoader({
        loading: true,
        message: 'Configuring Service Agreement...',
      }),
    );
    const businessRelationUid = getState().account.brUid;
    const partnerNotFound = getState().partner.partnerNotFoundForServiceRole;
    const partnerNotFoundComment = getState().partner.partnerNotFoundComment;

    if (!taskId) {
      dispatch(
        openModal({
          modalType: 'JBErrorModalWrapper',
          modalProps: {
            okHandler: () => {
              dispatch(closeModal());
            },
            technicalInfo: {
              title: 'Error when completing task',
              userErrorMessage: 'An error occurred when completing task (Task ID is required). Please try again later.',
            },
          },
        }),
      );
      dispatch(
        setLoader({
          loading: false,
          message: '',
        }),
      );
      return;
    }

    const serviceAgreementModified = await moveForwardServiceAgreementProcessApi(
      taskId,
      serviceAgreementUid,
      formData,
      action,
      undefined,
      partnerNotFoundComment,
      partnerNotFound,
    ).catch((error) => {
      dispatch(
        openModal({
          modalType: 'JBErrorModalWrapper',
          modalProps: {
            httpError: error,
            okHandler: () => {
              dispatch(closeModal());
            },
            technicalInfo: {
              title: 'Error when completing task',
              userErrorMessage: 'An error occurred when completing task. Please try again later.',
            },
          },
        }),
      );
      dispatch(
        setLoader({
          loading: false,
          message: '',
        }),
      );
      return;
    });

    if (serviceAgreementUid) {
      const serviceAgreementUpdated = await fetchServiceAgreement(
        serviceAgreementUid,
        serviceAgreementModified?.processInstanceId,
        dispatch,
      );

      dispatch(setSelectedServiceAgreement(serviceAgreementUpdated));
      dispatch(setSelectedServiceAgreementAttributes(parseServiceAgreementAttributes(serviceAgreementUpdated)));

      if (!businessRelationUid) return;
      dispatch(fetchAllServiceAgreementsForAccount(undefined, businessRelationUid, undefined));
    }
    dispatch(setPartnerNotFound(false));
    dispatch(setPartnerNotFoundComment(''));
    dispatch(
      setLoader({
        loading: false,
        message: '',
      }),
    );
  };

export const updateServiceAgreement =
  (serviceAgreement: ServiceAgreementV2, attributes: ServiceAgreementMetadata[]) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(
      setLoader({
        loading: true,
        message: 'Updating Service Agreement...',
      }),
    );
    const businessRelationUid = getState().account.brUid;
    const response = await updateServiceAgreementApi(serviceAgreement, attributes).catch((error) => {
      dispatch(
        openModal({
          modalType: 'JBErrorModalWrapper',
          modalProps: {
            httpError: error,
            okHandler: () => {
              dispatch(closeModal());
            },
            technicalInfo: {
              title: 'Error when trying to update Service Agreement',
              userErrorMessage: 'An error occurred when trying to update Service Agreement. Please try again later.',
            },
          },
        }),
      );
      dispatch(
        setLoader({
          loading: false,
          message: '',
        }),
      );
      return;
    });

    const serviceAgreementUpdated = await fetchServiceAgreement(
      serviceAgreement?.serviceAgreementId,
      response?.processInstanceId,
      dispatch,
    );

    dispatch(setSelectedServiceAgreement(serviceAgreementUpdated));
    dispatch(setSelectedServiceAgreementAttributes(parseServiceAgreementAttributes(serviceAgreementUpdated)));
    dispatch(
      setLoader({
        loading: false,
        message: '',
      }),
    );

    if (!businessRelationUid) return;
    dispatch(fetchAllServiceAgreementsForAccount(undefined, businessRelationUid, undefined));
  };

//TODO: Add partner flow.
export const closeServiceAgreement =
  (serviceAgreementUidList: string[], isBulk?: boolean, checked?: boolean) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(
      setLoader({
        loading: true,
        message:
          serviceAgreementUidList?.length > 1 ? 'Closing All Service Agreements...' : 'Closing Service Agreement...',
      }),
    );

    const brUid = getState().account.brUid;
    const contactNoteId = getState().serviceAgreements.contactNoteId;

    const selectedServiceAgreementId = getState().serviceAgreements.selectedServiceAgreement?.serviceAgreementId;

    if (brUid) {
      const closeServiceAgreementResult = await closeServiceAgreementsApi(
        brUid,
        serviceAgreementUidList,
        isBulk,
        contactNoteId,
        checked,
      ).catch((error) => {
        dispatch(
          openModal({
            modalType: 'JBErrorModalWrapper',
            modalProps: {
              httpError: error,
              okHandler: () => {
                dispatch(closeModal());
              },
              technicalInfo: {
                title: 'Error when closing Service Agreements',
                userErrorMessage: 'An error occurred when closing Service Agreements. Please try again later.',
              },
            },
          }),
        );
        dispatch(
          setLoader({
            loading: false,
            message: '',
          }),
        );
        return;
      });

      if (closeServiceAgreementResult && closeServiceAgreementResult.status === 200) {
        if (serviceAgreementUidList?.length === 1 && selectedServiceAgreementId) {
          const updatedServiceAgreement = await fetchServiceAgreement(selectedServiceAgreementId, undefined, dispatch);

          const tableData = _.cloneDeep(getState().serviceAgreements.tableData);

          const foundTableDataElementId = tableData.findIndex(
            (x) => x.serviceAgreementId === selectedServiceAgreementId,
          );

          tableData[foundTableDataElementId] = updatedServiceAgreement;
          dispatch(setTableData(tableData));

          dispatch(setSelectedServiceAgreement(updatedServiceAgreement));
        }

        if (brUid) {
          dispatch(fetchDocumentsToDistribute(brUid, null));
        }

        if (brUid && !selectedServiceAgreementId) {
          dispatch(fetchAllServiceAgreementsForAccount(undefined, brUid, undefined, true));
        }
      }
      dispatch(
        setLoader({
          loading: false,
          message: '',
        }),
      );
    } else {
      dispatch(
        setLoader({
          loading: false,
          message: '',
        }),
      );
    }
  };

export const getModificationData = (serviceAgreementId: string) => async (dispatch: AppDispatch) => {
  dispatch(
    setLoader({
      loading: true,
      message: 'Loading Service Agreement Configuration...',
    }),
  );
  const modificationData = await getModificationDataApi(serviceAgreementId).catch((error) => {
    dispatch(
      openModal({
        modalType: 'JBErrorModalWrapper',
        modalProps: {
          httpError: error,
          okHandler: () => {
            dispatch(closeModal());
          },
          technicalInfo: {
            title: 'Error when retrieving modification data of Service Agreement',
            userErrorMessage:
              'An error occurred when retrieving modification data Service Agreement. Please try again later.',
          },
        },
      }),
    );
    dispatch(
      setLoader({
        loading: false,
        message: '',
      }),
    );
    return;
  });

  if (modificationData) {
    dispatch(setModificationData(modificationData as ServiceAgreementModificationInformationResponse));
  }
  dispatch(
    setLoader({
      loading: false,
      message: '',
    }),
  );
};

export const sendModificationData =
  (
    serviceAgreementId: string,
    modificationData: ServiceAgreementModificationRequest,
    navigateToDetailsCallback: () => void,
  ) =>
  async (dispatch: AppDispatch) => {
    dispatch(
      setLoader({
        loading: true,
        message: 'Loading Modified Service Agreement...',
      }),
    );

    sendModificationDataApi(serviceAgreementId, modificationData)
      .then((response) => {
        dispatch(
          setLoader({
            loading: false,
            message: '',
          }),
        );
        const serviceAgreementResponse = response as ServiceAgreementV2;
        dispatch(setSelectedServiceAgreement(serviceAgreementResponse));
        dispatch(
          fetchAllServiceAgreementsForAccount(
            serviceAgreementResponse?.partnerId,
            serviceAgreementResponse?.brProxyId,
            undefined,
            false,
          ),
        );
        navigateToDetailsCallback();
      })
      .catch((error) => {
        dispatch(
          openModal({
            modalType: 'JBErrorModalWrapper',
            modalProps: {
              httpError: error,
              okHandler: () => {
                dispatch(closeModal());
              },
              technicalInfo: {
                title: 'Error when trying to modify Service Agreement',
                userErrorMessage: 'An error occurred when trying to modify Service Agreement. Please try again later.',
              },
            },
          }),
        );
        dispatch(
          setLoader({
            loading: false,
            message: '',
          }),
        );
        return;
      });
  };

export const closeAgreementAtALaterDate =
  (serviceAgreementUidList: string[], date: Date) => async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(
      setLoader({
        loading: true,
        message: 'Closing Service Agreement...',
      }),
    );
    const brUid = getState().account.brUid;

    if (!brUid) {
      dispatch(
        openModal({
          modalType: 'JBErrorModalWrapper',
          modalProps: {
            okHandler: () => {
              dispatch(closeModal());
            },
            technicalInfo: {
              title: 'Error when closing Service Agreement at later date',
              userErrorMessage:
                'An error occurred when closing Service Agreement at later date (Br ID is required). Please try again later.',
            },
          },
        }),
      );
      dispatch(
        setLoader({
          loading: false,
          message: '',
        }),
      );
      return;
    }

    const selectedServiceAgreementId = getState().serviceAgreements.selectedServiceAgreement?.serviceAgreementId;

    const closeServiceAgreementResult = await closeServiceAgreementsLaterDateApi(
      brUid,
      serviceAgreementUidList,
      date,
    ).catch((error) => {
      dispatch(
        openModal({
          modalType: 'JBErrorModalWrapper',
          modalProps: {
            httpError: error,
            okHandler: () => {
              dispatch(closeModal());
            },
            technicalInfo: {
              title: 'Error when closing Service Agreement',
              userErrorMessage: 'An error occurred when closing single Service Agreement. Please try again later.',
            },
          },
        }),
      );
      dispatch(
        setLoader({
          loading: false,
          message: '',
        }),
      );
      return;
    });

    if (closeServiceAgreementResult && closeServiceAgreementResult.status === 200) {
      const updatedServiceAgreement = await fetchServiceAgreement(selectedServiceAgreementId, undefined, dispatch);

      const tableData = _.cloneDeep(getState().serviceAgreements.tableData);

      const foundTableDataElementId = tableData.findIndex((x) => x.serviceAgreementId === selectedServiceAgreementId);

      tableData[foundTableDataElementId] = updatedServiceAgreement;
      dispatch(setTableData(tableData));

      dispatch(setSelectedServiceAgreement(updatedServiceAgreement));
      dispatch(
        setLoader({
          loading: false,
          message: '',
        }),
      );
    } else {
      dispatch(
        setLoader({
          loading: false,
          message: '',
        }),
      );
    }
  };

export const fetchContactNotesByBrUid = (brUid: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const partnerUid = getState().partner.partnerId;
  const contactNotes = await getContactNotesByBrUidApi(brUid ? brUid : partnerUid ? partnerUid : '').catch((error) => {
    dispatch(
      openModal({
        modalType: 'JBErrorModalWrapper',
        modalProps: {
          httpError: error,
          okHandler: () => {
            dispatch(closeModal());
          },
          technicalInfo: {
            title: 'Error when retrieving Contact notes',
            userErrorMessage: 'An error occurred when retrieving Contact notes. Please try again later.',
          },
        },
      }),
    );
    dispatch(
      setLoader({
        loading: false,
        message: '',
      }),
    );
    return;
  });

  if (contactNotes) {
    dispatch(setContactNotes(contactNotes));
  }
};

export const fetchServiceAgreement = async (
  serviceAgreementId: string | undefined,
  processInstanceId: string | undefined | null,
  dispatch: AppDispatch,
): Promise<ServiceAgreementV2> => {
  const result = await getServiceAgreementApi(serviceAgreementId, processInstanceId).catch((error) => {
    dispatch(
      openModal({
        modalType: 'JBErrorModalWrapper',
        modalProps: {
          httpError: error,
          okHandler: () => {
            dispatch(closeModal());
          },
          technicalInfo: {
            title: 'Error when retrieving single Service Agreement',
            userErrorMessage: 'An error occurred when retrieving single Service Agreement. Please try again later.',
          },
        },
      }),
    );
    dispatch(
      setLoader({
        loading: false,
        message: '',
      }),
    );
    return;
  });
  return result as ServiceAgreementV2;
};
